import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { CampaignStatusBadge, CampaignTypeBadge } from './CampaignStatusBadge';
import CampaignAnalyticsCards from './CampaignAnalyticsCards';
import RecipientTable from './RecipientTable';
import SendRunHistory from './SendRunHistory';
import AudiencePreviewPanel from './AudiencePreviewPanel';
import CampaignSendModal from './CampaignSendModal';
import CampaignTestSendModal from './CampaignTestSendModal';
import { FaEdit, FaPaperPlane, FaEnvelope } from 'react-icons/fa';

function fetchCampaign(id) {
    return axios.get(`/api/campaigns/${id}/`, { withCredentials: true }).then((r) => r.data);
}

function fetchAnalytics(id) {
    return axios.get(`/api/campaigns/${id}/analytics/`, { withCredentials: true }).then((r) => r.data);
}

function fetchRecipients(id, { page, search, status: s }) {
    const params = new URLSearchParams({ page });
    if (search) params.set('search', search);
    if (s) params.set('status', s);
    return axios.get(`/api/campaigns/${id}/recipients/?${params}`, { withCredentials: true }).then((r) => r.data);
}

function fetchAudience(id) {
    return axios.get(`/api/campaigns/${id}/audience/`, { withCredentials: true }).then((r) => r.data);
}

export default function CampaignDetailPage() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [activeTab, setActiveTab] = useState('overview');
    const [showSendModal, setShowSendModal] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [recipientFilter, setRecipientFilter] = useState({ page: 1, search: '', status: '' });

    const { data: campaign, isLoading, error } = useQuery({
        queryKey: ['campaign', campaignId],
        queryFn: () => fetchCampaign(campaignId),
    });

    const { data: analytics } = useQuery({
        queryKey: ['campaign-analytics', campaignId],
        queryFn: () => fetchAnalytics(campaignId),
        enabled: activeTab === 'overview',
        staleTime: 30_000,
    });

    const { data: recipientData } = useQuery({
        queryKey: ['campaign-recipients', campaignId, recipientFilter],
        queryFn: () => fetchRecipients(campaignId, recipientFilter),
        enabled: activeTab === 'recipients',
        keepPreviousData: true,
    });

    const { data: audienceData } = useQuery({
        queryKey: ['campaign-audience', campaignId],
        queryFn: () => fetchAudience(campaignId),
        enabled: activeTab === 'audience',
    });

    const canSend = campaign?.status === 'ready';
    const isSending = campaign?.status === 'sending';
    const isSent = campaign?.status === 'sent';

    if (isLoading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border" role="status" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">Failed to load campaign.</div>
                </div>
            </div>
        );
    }

    return (
        <>
            <button
                className="btn btn-link btn-sm ps-0 mb-3"
                onClick={() => navigate('/league-manager/campaigns/')}
            >
                ← All campaigns
            </button>

            {/* Header */}
            <div className="card mb-3">
                <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div>
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                <h5 className="mb-0 fw-bold">{campaign.title}</h5>
                                <CampaignStatusBadge status={campaign.status} />
                                <CampaignTypeBadge type={campaign.campaign_type} />
                            </div>
                            <p className="small text-muted mb-1">{campaign.subject || 'No subject set'}</p>
                            <div className="small text-muted">
                                {campaign.league_name}
                                {campaign.season_name && ` · ${campaign.season_name}`}
                                {campaign.team_name && ` · ${campaign.team_name}`}
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            {!isSent && !isSending && (
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => navigate(`/league-manager/campaigns/${campaignId}/edit/`)}
                                >
                                    <FaEdit className="me-1" /> Edit
                                </button>
                            )}
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowTestModal(true)}
                            >
                                <FaEnvelope className="me-1" /> Test send
                            </button>
                            {canSend && (
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setShowSendModal(true)}
                                >
                                    <FaPaperPlane className="me-1" /> Send
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card">
                <div className="card-header border-0 pb-0">
                    <ul className="nav nav-tabs card-header-tabs">
                        {['overview', 'recipients', 'audience', 'activity'].map((tab) => (
                            <li key={tab} className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="card-body">
                    {activeTab === 'overview' && (
                        <div>
                            <CampaignAnalyticsCards analytics={analytics} />
                            {campaign.sent_at && (
                                <p className="small text-muted mt-3 mb-0">
                                    Sent {new Date(campaign.sent_at).toLocaleString()}
                                    {campaign.sent_by_name && ` by ${campaign.sent_by_name}`}
                                </p>
                            )}
                        </div>
                    )}

                    {activeTab === 'recipients' && (
                        <RecipientTable
                            campaignId={campaignId}
                            recipients={recipientData?.results || []}
                            total={recipientData?.count || 0}
                            page={recipientFilter.page}
                            pageSize={50}
                            onPageChange={(p) =>
                                setRecipientFilter((prev) => ({ ...prev, page: p }))
                            }
                            onFilter={({ search, status: s }) =>
                                setRecipientFilter({ page: 1, search, status: s })
                            }
                        />
                    )}

                    {activeTab === 'audience' && (
                        <div>
                            <div className="mb-4" style={{ maxHeight: 480, display: 'flex', flexDirection: 'column' }}>
                                <AudiencePreviewPanel
                                    loading={!audienceData}
                                    summary={audienceData?.summary}
                                    included={audienceData?.included || []}
                                    excludedEmails={campaign.manually_excluded_emails || []}
                                />
                            </div>
                            <h6 className="fw-semibold small text-muted text-uppercase mb-2">Audience configuration</h6>
                            <div className="row g-2 small">
                                {[
                                    { key: 'include_league_players', label: 'League players' },
                                    { key: 'include_season_players', label: 'Season players' },
                                    { key: 'include_team_players', label: 'Team players' },
                                    { key: 'include_selected_players', label: 'Selected players' },
                                    { key: 'include_subscribers', label: 'Subscribers' },
                                    { key: 'include_previous_players', label: 'Previous players' },
                                ].map(({ key, label }) => (
                                    <div key={key} className="col-6 col-md-4">
                                        <span className={campaign[key] ? 'text-success' : 'text-muted'}>
                                            {campaign[key] ? '✓' : '○'} {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'activity' && (
                        <SendRunHistory sendRuns={analytics?.send_runs} />
                    )}
                </div>
            </div>

            <CampaignSendModal
                show={showSendModal}
                campaign={campaign}
                audienceSummary={audienceData?.summary}
                onClose={() => setShowSendModal(false)}
                onSent={() => {
                    setShowSendModal(false);
                    queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
                    queryClient.invalidateQueries({ queryKey: ['campaign-analytics', campaignId] });
                }}
            />

            <CampaignTestSendModal
                show={showTestModal}
                campaign={campaign}
                onClose={() => setShowTestModal(false)}
            />
        </>
    );
}
